/** Condition/certified dependency rules for feed-backed Edit Vehicle. */

export function normalizeConditionToken(condition: unknown): 'New' | 'Used' | '' {
  const raw = String(condition ?? '').trim()
  if (!raw) return ''
  const upper = raw.toUpperCase()
  if (upper === 'N' || upper === 'NEW' || raw.toLowerCase() === 'new') return 'New'
  if (
    upper === 'U' ||
    upper === 'USED' ||
    upper === 'C' ||
    upper === 'CPO' ||
    raw.toLowerCase() === 'used' ||
    raw.toLowerCase() === 'cpo'
  ) {
    return 'Used'
  }
  return ''
}

export function isCertifiedAllowedForCondition(condition: unknown): boolean {
  return normalizeConditionToken(condition) === 'Used'
}

export function isTruthyValue(value: unknown): boolean {
  if (value === true) return true
  if (value === false || value == null) return false
  if (typeof value === 'number') return value !== 0
  const normalized = String(value).trim().toLowerCase()
  return normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'y'
}

type PendingCertifiedAction = {
  action?: string
  manualValue?: unknown
} | null | undefined

export type CertifiedConditionContext = {
  isFeedBacked: boolean
  condition: unknown
  pendingCertifiedAction?: PendingCertifiedAction
  certifiedLocallyDirty?: boolean
  localCertifiedValue?: unknown
}

export function getCertifiedNewConditionConflictMessage(
  ctx: CertifiedConditionContext,
): string | null {
  if (!ctx.isFeedBacked || isCertifiedAllowedForCondition(ctx.condition)) return null

  const pending = ctx.pendingCertifiedAction
  if (pending && pending.action !== 'REMOVE_OVERRIDE' && isTruthyValue(pending.manualValue)) {
    return 'A pending certified override requires Used condition. Revert the certified override or set condition to Used before saving.'
  }

  if (ctx.certifiedLocallyDirty && isTruthyValue(ctx.localCertifiedValue)) {
    return 'Certification is only available for used vehicles. Set condition to Used or revert the certified change before saving.'
  }

  return null
}

export function validateCertifiedConditionOnSave(
  ctx: CertifiedConditionContext & { dirtyFields: Record<string, unknown> },
): string | null {
  const conflict = getCertifiedNewConditionConflictMessage(ctx)
  if (conflict) return conflict

  if (!ctx.isFeedBacked || isCertifiedAllowedForCondition(ctx.condition)) return null

  if (
    'certified_by_dealer' in ctx.dirtyFields &&
    isTruthyValue(ctx.dirtyFields.certified_by_dealer)
  ) {
    return 'Cannot save: certification is only available for used vehicles.'
  }

  return null
}

export function shouldExcludeCertifiedOverrideDirty(input: {
  isFeedBacked: boolean
  field: string
  condition: unknown
}): boolean {
  return (
    input.isFeedBacked &&
    input.field === 'certified_by_dealer' &&
    !isCertifiedAllowedForCondition(input.condition)
  )
}
